/**
 * DatasetCreatorDialog Component
 *
 * A dialog for creating new datasets from InfluxDB data.
 * Integrates TimeRangeSelector, MeasurementBrowser, and DataPreview.
 */

import { useState, useEffect } from "react";
import { isValid } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useFactory } from "@/core/hooks/useFactory";
import { useGlobalTimeRange } from "@/core/hooks/useGlobalTimeRange";
import { Database } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Label } from "@/shared/ui/label";
import { Badge } from "@/shared/ui/badge";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/shared/ui/resizable";
import { TimeRangeSelector } from "@/shared/ui/time-range-selector";
import { MeasurementBrowser } from "./dataset-creator/MeasurementBrowser";
import { DataPreview } from "./dataset-creator/DataPreview";
import { UNSBrowser } from "./dataset-creator/UNSBrowser";
import { createDataset } from "../api/datasets";
import type {
  MeasurementFieldSelections,
  DatasetCreateRequest,
  SelectedDatapoint,
  DatasetBrowseMode,
} from "../types/dataset";

interface DataSource {
  id: string;
  name: string;
  source_type: string;
  config: {
    url?: string;
    org?: string;
    bucket?: string;
  };
}

interface DatasetCreatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DatasetCreatorDialog({ open, onOpenChange }: DatasetCreatorDialogProps) {
  const queryClient = useQueryClient();
  const { selectedFactoryId } = useFactory();

  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>("");
  const [selectedBucket, setSelectedBucket] = useState<string>("");
  const { timeSelection, setTimeSelection, dateRange } = useGlobalTimeRange();
  const [selections, setSelections] = useState<MeasurementFieldSelections>({});

  // UNS mode state
  const [browseMode, setBrowseMode] = useState<DatasetBrowseMode>("uns");
  const [unsSelections, setUnsSelections] = useState<Map<string, SelectedDatapoint>>(
    new Map()
  );
  const [lockedBucket, setLockedBucket] = useState<string | null>(null);

  // Fetch InfluxDB data sources
  const { data: dataSources = [] } = useQuery({
    queryKey: ["core", "data-sources", "influxdb", selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];

      // First get factory to get tenant_id
      const { data: factory } = await supabase
        .schema("core")
        .from("factories")
        .select("tenant_id")
        .eq("id", selectedFactoryId)
        .single();

      if (!factory?.tenant_id) return [];

      const { data, error } = await supabase
        .schema("core")
        .from("data_sources")
        .select("id, name, source_type, config")
        .eq("tenant_id", factory.tenant_id)
        .eq("source_type", "influxdb_v2")
        .eq("is_active", true);

      if (error) throw error;
      return (data || []) as DataSource[];
    },
    enabled: open && !!selectedFactoryId,
  });

  // Fetch buckets for selected data source
  const { data: buckets = [], isLoading: loadingBuckets } = useQuery({
    queryKey: ["influxdb", "buckets", selectedDataSourceId],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("influxdb-browser", {
        body: { action: "buckets", dataSourceId: selectedDataSourceId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return (data?.data || []) as string[];
    },
    enabled: !!selectedDataSourceId,
  });

  // Create dataset mutation
  const { mutate: createDatasetMutate, isPending: isCreating } = useMutation({
    mutationFn: createDataset,
    onSuccess: (data, variables) => {
      const datasetName = data.name || variables.name;
      toast.success(`Dataset "${datasetName}" created successfully`);
      queryClient.invalidateQueries({ queryKey: ["ai", "datasets"] });
      onOpenChange(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(`Failed to create dataset: ${error.message}`);
    },
  });

  // Reset form when dialog closes
  const resetForm = () => {
    setSelectedDataSourceId("");
    setSelectedBucket("");
    setSelections({});
    setUnsSelections(new Map());
    setLockedBucket(null);
    setBrowseMode("uns");
  };

  // Handle UNS selection changes
  const handleUnsSelectionChange = (
    newSelections: Map<string, SelectedDatapoint>
  ) => {
    const first = newSelections.values().next().value;
    setLockedBucket(first?.bucketName ?? null);
    setUnsSelections(newSelections);
  };

  // Convert UNS selections to preview format
  const convertUnsToPreview = (
    uns: Map<string, SelectedDatapoint>
  ): MeasurementFieldSelections => {
    const result: MeasurementFieldSelections = {};
    for (const dp of uns.values()) {
      if (!result[dp.measurementName]) {
        result[dp.measurementName] = [];
      }
      result[dp.measurementName].push(dp.fieldName);
    }
    return result;
  };

  // Handle removing a field in UNS mode
  const handleUnsRemoveField = (measurement: string, field: string) => {
    const newSelections = new Map(unsSelections);
    for (const [key, dp] of newSelections) {
      if (dp.measurementName === measurement && dp.fieldName === field) {
        newSelections.delete(key);
        break;
      }
    }
    setUnsSelections(newSelections);
    if (newSelections.size === 0) {
      setLockedBucket(null);
    }
  };

  const getComputedRange = (): { start: Date; end: Date } => {
    const fallback = {
      start: new Date(Date.now() - 24 * 60 * 60 * 1000),
      end: new Date(),
    };
    if (!isValid(dateRange.start) || !isValid(dateRange.end)) {
      return fallback;
    }
    return dateRange;
  };

  // Reset selections when data source or bucket changes
  useEffect(() => {
    setSelections({});
  }, [selectedDataSourceId, selectedBucket]);

  // Auto-select first data source
  useEffect(() => {
    if (dataSources.length > 0 && !selectedDataSourceId) {
      setSelectedDataSourceId(dataSources[0].id);
    }
  }, [dataSources, selectedDataSourceId]);

  // Auto-select first bucket
  useEffect(() => {
    if (buckets.length > 0 && !selectedBucket) {
      setSelectedBucket(buckets[0]);
    }
  }, [buckets, selectedBucket]);

  const handleRemoveField = (measurement: string, field: string) => {
    const currentFields = selections[measurement] || [];
    const newFields = currentFields.filter((f) => f !== field);

    const newSelections = { ...selections };
    if (newFields.length === 0) {
      delete newSelections[measurement];
    } else {
      newSelections[measurement] = newFields;
    }

    setSelections(newSelections);
  };

  const handleCreate = (name: string, limit?: number) => {
    let measurements: string[];
    let fields: string[];
    let connectorName: string;

    if (browseMode === "uns") {
      const datapoints = Array.from(unsSelections.values());
      measurements = [...new Set(datapoints.map((d) => d.measurementName))];
      fields = datapoints.map((d) => d.fieldName);
      connectorName = `@InfluxDB.${lockedBucket}`;
    } else {
      measurements = Object.keys(selections);
      fields = Object.values(selections).flat();
      connectorName = `@InfluxDB.${selectedBucket}`;
    }

    // Calculate time range
    const { start: startTime, end: endTime } = getComputedRange();

    const request: DatasetCreateRequest = {
      name,
      measurement: measurements,
      fields,
      startTime: startTime.toISOString(),
      endTime: endTime.toISOString(),
      limit,
      connectorName,
    };

    createDatasetMutate(request);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl h-[80vh] flex flex-col p-0">
        <DialogHeader className="p-6 pb-4 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Create New Dataset
          </DialogTitle>
          <DialogDescription>
            Select measurements and fields from InfluxDB to create a dataset for AI training.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Configuration Row */}
          <div className="px-6 py-4 border-b bg-muted/30 flex flex-wrap items-center gap-4">
            {/* Mode Toggle */}
            <Tabs
              value={browseMode}
              onValueChange={(v) => setBrowseMode(v as DatasetBrowseMode)}
            >
              <TabsList>
                <TabsTrigger value="uns">Unified Namespace</TabsTrigger>
                <TabsTrigger value="raw">Raw Data Source</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Show Data Source + Bucket selectors only in raw mode */}
            {browseMode === "raw" && (
              <>
                <div className="flex items-center gap-2">
                  <Label className="text-sm whitespace-nowrap">Data Source</Label>
                  <Select value={selectedDataSourceId} onValueChange={setSelectedDataSourceId}>
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select data source" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataSources.map((ds) => (
                        <SelectItem key={ds.id} value={ds.id}>
                          {ds.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedDataSourceId && (
                  <div className="flex items-center gap-2">
                    <Label className="text-sm whitespace-nowrap">Bucket</Label>
                    <Select
                      value={selectedBucket}
                      onValueChange={setSelectedBucket}
                      disabled={loadingBuckets}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder={loadingBuckets ? "Loading..." : "Select bucket"} />
                      </SelectTrigger>
                      <SelectContent>
                        {buckets.map((bucket) => (
                          <SelectItem key={bucket} value={bucket}>
                            {bucket}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </>
            )}

            {/* Show locked bucket indicator in UNS mode */}
            {browseMode === "uns" && lockedBucket && (
              <Badge variant="secondary">Bucket: {lockedBucket}</Badge>
            )}

            {/* Time Range Selector - always visible */}
            <TimeRangeSelector value={timeSelection} onChange={setTimeSelection} />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ResizablePanelGroup direction="horizontal" className="min-h-[520px]">
              <ResizablePanel defaultSize={40} minSize={25} className="min-h-0">
                {browseMode === "uns" ? (
                  <UNSBrowser
                    factoryId={selectedFactoryId}
                    selections={unsSelections}
                    onSelectionChange={handleUnsSelectionChange}
                    lockedBucket={lockedBucket}
                    className="h-full"
                  />
                ) : (
                  <MeasurementBrowser
                    dataSourceId={selectedDataSourceId}
                    bucket={selectedBucket}
                    selections={selections}
                    onSelectionChange={setSelections}
                    className="h-full"
                  />
                )}
              </ResizablePanel>
              <ResizableHandle withHandle />
              <ResizablePanel defaultSize={60} className="min-h-0">
                <ScrollArea className="h-full">
                  <DataPreview
                    bucket={browseMode === "uns" ? lockedBucket || "" : selectedBucket}
                    selections={
                      browseMode === "uns"
                        ? convertUnsToPreview(unsSelections)
                        : selections
                    }
                    onRemoveField={
                      browseMode === "uns" ? handleUnsRemoveField : handleRemoveField
                    }
                    onSave={handleCreate}
                    isSaving={isCreating}
                    className="min-h-full"
                    unsSelections={browseMode === "uns" ? unsSelections : undefined}
                    rawSelections={browseMode === "raw" ? selections : undefined}
                    dataSourceId={browseMode === "raw" ? selectedDataSourceId : undefined}
                    startTime={getComputedRange().start}
                    endTime={getComputedRange().end}
                  />
                </ScrollArea>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DatasetCreatorDialog;
